/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { PureComponent } from 'react';
import classNames from 'clsx';
import { oneOf, string, bool, shape, func, number } from 'prop-types';
import { connect } from 'react-redux';
import uuid from 'uuid/v1';

import {
  ADD_PICTURE_SUCCESS,
  addPicture as addPictureAction,
} from 'actions/user';

import {
  UPLOAD_IMAGE_SUCCESS,
  UPLOAD_IMAGE_FAILURE,
  uploadImage as uploadImageAction,
} from 'actions/cloudinary';

import Spinner from 'components/Spinner/Spinner';

import AddIcon from 'gfx/vector/add-icon.svg';
import ImageIcon from 'gfx/vector/image-icon.svg';

import styles from './image.scss';

/**
 * This is the Image component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */
export class Image extends PureComponent {
  /**
   * Declare expected prop types.
   *
   * @type {Object}
   */
  static propTypes = {
    type: oneOf(['image', 'file']).isRequired,
    auth: shape({
      token: string.isRequired,
    }).isRequired,
    user: shape({
      type: string.isRequired,
    }).isRequired,
    uploadImage: func.isRequired,
    addPicture: func.isRequired,
    name: string.isRequired,
    index: number,
    // placeholder: string,
    value: string,
    preview: string,
    // required: bool,
    readOnly: bool,
    error: bool,
    onChange: func,
  };

  /**
   * Declare default props.
   *
   * @type {Object}
   */
  static defaultProps = {
    placeholder: '',
    value: '',
    preview: '',
    required: false,
    error: false,
    readOnly: false,
    onChange: () => {},
  };

  /**
   * Create Image.
   */
  constructor(props) {
    super(props);

    this.state = {
      sending: false,
      error: null,
      title: props.value,
      preview: props.preview,
    };
  }

  onDragOver = event => {
    event.stopPropagation();
    event.preventDefault();

    event.dataTransfer.dropEffect = 'copy';
  };

  onDrop = event => {
    event.stopPropagation();
    event.preventDefault();

    this.handleFile(event.dataTransfer.files[0]);
  };

  selectFile = event => {
    event.stopPropagation();
    event.preventDefault();

    this.handleFile(this.fileInput.files[0]);
  };

  handleFile(file) {
    const {
      auth: { token },
      user,
      type: inputType,
      index,
      name,
      value: initialTitle,
      preview: initialPreview,
      onChange,
      uploadImage,
      addPicture,
    } = this.props;

    if (!file) {
      return;
    }

    if (['image/jpeg', 'image/gif', 'image/png'].indexOf(file.type) === -1) {
      this.setState({ error: 'Not a valid image' });
      return;
    }

    const key = uuid() + /(?:\.([^.]+))?$/.exec(file.name)[0];

    this.setState({
      sending: true,
      title: '',
      preview: '',
    });

    const context = [`user_id=${user.user_id}`, `input_id=${name}`];

    uploadImage({
      key,
      file,
      context,
      contentType: file.type,
    }).then(({ type, payload }) => {
      if (type === UPLOAD_IMAGE_SUCCESS) {
        // only for index and type image.
        if (index >= 0 && inputType === 'image') {
          addPicture(token, {
            role_type: user.type,
            indexes: [index],
            cloudinary_public_ids: [payload.key],
          }).then(addPictureAction => {
            if (addPictureAction.type === ADD_PICTURE_SUCCESS) {
              onChange({ target: { value: payload.url } });

              this.setState({
                sending: false,
                title: file.name,
                preview: payload.url,
              });
            }
          });
        } else {
          this.setState({
            sending: false,
            title: file.name,
            preview: payload.url,
          });

          onChange({ target: { value: payload.key } });
        }
      }

      // @NOTE: Handle error the right way.
      if (type === UPLOAD_IMAGE_FAILURE) {
        this.setState({
          title: initialTitle,
          preview: initialPreview,
          sending: false,
        });
      }
    });
  }

  /**
   * Render Image.
   *
   * @return {JSX}
   */
  render() {
    const { title, preview, sending } = this.state;
    const { name, readOnly, error } = this.props;

    return (
      <div
        className={classNames(styles.wrapper, {
          [styles.sending]: sending,
          [styles.error]: error,
          [styles.readOnly]: readOnly,
        })}
        onDragOver={this.onDragOver}
        onDrop={this.onDrop}
      >
        <div className={styles.inner}>
          <div className={styles.meta}>
            {preview && (
              <div
                className={styles.previewImage}
                style={{ backgroundImage: `url(${preview})` }}
              />
            )}

            {!sending && (
              <div>
                {!preview && (
                  <div className={styles.imageIcon}>
                    <ImageIcon />
                  </div>
                )}

                <div className={styles.addIcon}>
                  <AddIcon />
                </div>
              </div>
            )}

            {sending && <Spinner small />}
          </div>

          <div className={styles.title}>
            <span>{title}</span>
          </div>
        </div>

        <input
          ref={ref => {
            this.fileInput = ref;
          }}
          id={name}
          type="file"
          name={name}
          className={styles.input}
          onChange={this.selectFile}
        />
      </div>
    );
  }
}

export default connect(
  ({ auth, user }) => ({ auth, user }),
  {
    uploadImage: uploadImageAction,
    addPicture: addPictureAction,
  }
)(Image);
